/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {AnnotationService} from './annotation.service';
import {SpatialUtilService} from '../spatial-util.service';
import {MapEventsService} from '../map-events.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {MapModeService} from '../map-mode.service';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {AngleUtilService} from './angle/angle-util.service';

@Injectable({
    providedIn: 'root'
})
export class AffectedLotService {
    title = 'AffectedLotService';

    handler: any;

    constructor(
        private angleUtilService: AngleUtilService,
        private annotationService: AnnotationService,
        private destroyRef: DestroyRef,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private mapEventsService: MapEventsService,
        private mapModeService: MapModeService,
        private planModelService: PlanModelService,
        private printAreaService: PrintAreaService,
        private spatialUtilService: SpatialUtilService
    ) {
        this.eventBus
            .on('addAffectedLotAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on - addAffectedLotAnnotationAssociation`);
                const feature = meta.data;

                const attributes = {
                    featuretype: 'affectedLot',
                    featureid: feature.id,
                    featuresubtype: feature.type, // Note: No type is defined for affectedLot
                    guid: this.planModelService.getGuid(),
                    rotation: 90.0,
                    rotationdms: '90:00:00'
                };

                const attributesGroup = {};

                this.annotationService.getAnnotationConfigurations('affectedLot', 1).forEach(ac => {
                    attributes[ac.annotationAttribute] = feature[ac.featureAttribute];
                    attributesGroup[ac.annotationAttribute] = !!ac.parent;
                });

                this.create(attributes, attributesGroup);
            });

        this.eventBus
            .on('removeAffectedLotAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.deleteHandler());
    }

    create(attributes: any, attributesGroup: any) {
        console.debug(`${this.title} - create`);

        this.spatialUtilService.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

        this.handler = this.spatialUtilService.map.on('click', (event: any) => {
            this.deleteHandler();
            this.createAffectedLotAnnotation(attributes, attributesGroup, event.mapPoint).subscribe(() => {
                this.mapEventsService.resetMapClickHandler(this.spatialUtilService.map);
                this.eventBus.cast('stopaffectedLotAnnotationTool');
                this.spatialUtilService.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
            });
        });
    }

    private deleteHandler() {
        if (this.handler) {
            this.handler.remove();
        }
    }

    private createAffectedLotAnnotation(attributes: any, attributesGroup: any, mapPoint: any) {
        console.debug(`${this.title} - createAffectedLotAnnotation`);

        let isGroup = !Object.values(attributesGroup).some(v => v === false);

        const baseOffset = this.printAreaService.getReferenceScaleDivisor() * 0.004;

        const annotationsToBeCreated = [];

        for (const [groupKey, groupValue] of Object.entries(attributesGroup) as any[]) {
            if (groupValue === false || isGroup) {
                const attributeNumber = groupKey.replace(/^\D+/g, '');
                const offsetY = isGroup ? 0 : baseOffset * (attributeNumber === '' ? 0 : parseInt(attributeNumber) - 1);

                const pointGeometry = new this.esriModuleService.geometry.Point(mapPoint.x, mapPoint.y - offsetY);
                pointGeometry.setSpatialReference(mapPoint.spatialReference);

                const pointAttributes: {x: number; y: number; rotation?: number; rotationdms?: string} = {x: mapPoint.x, y: mapPoint.y};

                for (const [attributeKey, attribute] of Object.entries(attributes) as any[]) {
                    if (attribute && typeof attribute === 'object') {
                        pointAttributes[attributeKey] = attribute.id;
                    } else if (attributesGroup[attributeKey] || attributeKey == groupKey || typeof attributesGroup[attributeKey] === 'undefined') {
                        pointAttributes[attributeKey] = attribute;
                    }
                }

                if (this.mapModeService.isMapModeType('enlargement')) {
                    pointAttributes['enlargementid'] = this.mapModeService.mapMode.id;
                }

                const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
                if (pointAttributes.rotationdms) {
                    pointAttributes.rotation = this.angleUtilService.dmsToDd(pointAttributes.rotationdms);
                }
                pointGraphic.attributes = pointAttributes;
                annotationsToBeCreated.push(pointGraphic);

                isGroup = false;
            }
        }

        return this.annotationService.populateLabelExpressionAndCreateAnnotations(annotationsToBeCreated);
    }
}
